// String constants for the application

// Common strings
export const COMMON = {
  LOADING: 'Loading...',
  BACK: 'Back',
  UNKNOWN: 'Unknown',
  NA: 'N/A',
  ACCESS_DENIED: 'Access Denied',
  ACCESS_DENIED_MESSAGE: 'You do not have permission to view this page.',
  RETURN_HOME: 'Return to Home',
};

// Report types
export const REPORT_TYPES = {
  DOR: {
    TITLE: 'Daily Observation Report',
    SHORT: 'DOR',
    DESCRIPTION: 'Record daily observations for trainees.',
  },
  EOWR: {
    TITLE: 'End of Week Report',
    SHORT: 'EOWR',
    DESCRIPTION: "Summarize the week's training progress.",
  },
  OSR: {
    TITLE: 'Officer Safety Report',
    SHORT: 'OSR',
    DESCRIPTION: 'Report any officer safety incidents.',
  },
};

// PDF Generation
export const PDF = {
  DEPARTMENT: {
    NAME: 'CHEVERLY POLICE DEPARTMENT',
    ADDRESS: '6401 FOREST ROAD, CHEVERLY, MD 20785',
  },
  TRAINING_MANUAL: {
    TITLE: 'Field Training Manual',
    SENSITIVE_INFO: 'INFORMATION IN THIS BOOKLET IS LAW ENFORCEMENT SENSITIVE AND IS NOT FOR PUBLIC DISSEMINATION',
  },
  MEMO: {
    TITLE: 'Memo',
    TO: 'David Morris, Chief of Police',
    SUBJECT: 'Completion of Field Training',
    MPCTC_NOTIFICATION: 'Notification to MPCTC has been made in order to update their records.',
    SIGNATURES: {
      FTO: 'Field Training Officer',
      SUPERVISOR: 'Supervisor',
      CHIEF: 'Chief of Police',
    },
  },
};

// Error messages
export const ERRORS = {
  FETCH_FAILED: 'Failed to load data',
  GENERATE_PDF_FAILED: 'Failed to generate PDF',
  GENERATE_MEMO_FAILED: 'Failed to generate memo',
  NO_DATA_PDF: 'No data available to generate PDF',
  FETCH_IMAGE_FAILED: 'Failed to load banner image for PDF',
  COMPILE_REPORTS_FAILED: 'Failed to compile reports PDF',
  NO_REPORTS_TO_COMPILE: 'No reports found for this trainee to compile.',
};

// Success messages
export const SUCCESS = {
  PDF_GENERATED: 'PDF generated successfully',
  MEMO_GENERATED: 'Memo generated successfully',
  COMPILE_REPORTS_GENERATED: 'Compiled reports PDF generated successfully',
};

// Confirmation messages
export const CONFIRM = {
  GENERATE_COVER: (name) => `Are you sure you want to generate a cover page for ${name}?`,
  GENERATE_MEMO: (name) => `Are you sure you want to generate a completion memo for ${name}?`,
  COMPILE_REPORTS: (name) => `Compile all reports for ${name} into a single PDF? This may take a moment.`,
};

// Button labels
export const BUTTONS = {
  CREATE_REPORT: 'Create Report',
  VIEW_REPORTS: 'View Reports',
  COVER_PAGE: 'Cover Page',
  COMPLETION_MEMO: 'Completion Memo',
  DOWNLOAD_PDF: 'Download PDF',
  COMPILE_REPORTS: 'Compile All Reports',
};

// Stats labels
export const STATS = {
  TOTAL_HOURS: 'Total Hours',
  TRAINING_PERIOD: 'Training Period',
  DAYS_IN_TRAINING: 'Days in Training',
  DORS_WRITTEN: 'DORs Written',
  REQUIRED_HOURS: 'Required Hours',
};

// Search related
export const SEARCH = {
  TRAINEE_PLACEHOLDER: 'Search your reports...',
  ADMIN_PLACEHOLDER: 'Search reports...',
  NO_RESULTS: 'No reports found matching your search.',
};

// File names
export const FILE_NAMES = {
  TRAINING_MANUAL: (name) => `Field_Training_Manual_${name}`,
  COMPLETION_MEMO: (name, id) => `Completion_Memo_${name}_${id}`,
  COMPILED_REPORTS: (name, id) => `Compiled_Reports_${name}_${id}`,
};

// Modal titles
export const MODAL_TITLES = {
  GENERATE_COVER: 'Generate Cover Page',
  GENERATE_MEMO: 'Generate Completion Memo',
  COMPILE_REPORTS: 'Compile Reports PDF',
};

// Status messages
export const STATUS = {
  NO_DORS: 'No DORs found.',
};

// Protocol Content
export const PROTOCOL = {
  CONTENT: `
    Field Training Officer Program
  
  ---
  
  Introduction
  Our department values the individuals who make it special, each having successfully completed the Field Training Officer Program. This program serves as a crucial blend of professional policing techniques and departmental philosophy, underscoring our dedication to high standards in public service.
  
  ---
  
  Purpose of the Field Training Officer Program
  The Field Training Officer Program aims to enhance police services by ensuring a rigorous selection of personnel, maintaining high performance standards, using objective evaluation methods, and fostering strong management involvement.
  
  ---
  
  Duties and Responsibilities
  This section outlines roles and responsibilities in the training process. Each participant's role is designed to contribute to achieving departmental goals and maintaining commitment to professional standards.
  
  ---
  
  Probationary Officers
  Probationary officers are expected to enter training with a thorough understanding of departmental policies, an academic foundation, and commitment. During the fifty-day program, they engage in a student-teacher relationship with the Field Training Officer (FTO), transitioning from classroom learning to real-world applications.
  
  - Phases of Training: The training is divided into two phases, each lasting 25 working days with different FTOs.
  - Evaluation: Probationary officers undergo daily evaluations on task performance.
  - Attendance: Two days of leave are allowed during the program.
  
  ---
  
  Restrictions While in Field Training
  Probationary officers must be supervised by the FTO during duty. Off-duty conduct, such as taking police actions, must be limited to emergencies.
  
  - Vehicle Operation: Officers may ride with other officers while off duty but cannot operate police vehicles unless in emergencies.
  - Radio Use: Radio communications are restricted to emergencies when off duty.
  - Program Limitations: Officers in training cannot participate in the Ride-Along Program, secondary employment, or certain overtime.
  
  ---
  
  Debriefing and Remedial Training
  
  Debriefing  
  A review meeting takes place after the first 25 working days with the FTOs, probationary officer, and patrol supervisor.
  
  Remedial Training  
  If deficiencies arise, remedial training may be coordinated through the Supervisor and Chief of Police. Methods include:
  
  - Repetition: Revisiting less satisfactory activities.
  - Observation: Allowing probationary officers to observe FTOs.
  - Partial Responsibility: Assigning tasks to focus on problem areas.
  - Verbalization: Officers may discuss situations to improve awareness.
  
  Outside training is explored if necessary, ensuring instructor qualifications meet standards.
  
  ---
  
  Extensions and Termination in Field Training
  
  - Extensions: The 240-hour program is a minimum, with extensions possible for additional training as deemed necessary.
  - Termination: If performance issues persist, termination may be considered following attempts at remedial training and extensions.
  
  ---
  
  Requirements for Release from Field Training
  To be released from the program, probationary officers must:
  
  - Complete 240 working hours.
  - Achieve satisfactory ratings in evaluations.
  - Meet all departmental requirements, including document completion and Chief of Police review.
  
  ---
  
  Evaluation Criteria
  Field Training Officers use specific guidelines to evaluate probationary officers. Lower ratings require justification and documented steps for improvement.
  
  ---
  
  Returning Officers and Experienced Officers
  Returning Cheverly officers and experienced police officers complete the Field Training Program upon rejoining, with requirements based on prior service duration.
  
  ---
  
  Interruptions in Field Training
  Field Training may be interrupted due to:
  
  - Military Duty: Probationary periods may extend for active duty.
  - Injury or Light Duty: Continuation is based on injury severity.
  
  Disputes under this section are resolved by the department.
  
  ---
  
  Roles and Incentives for Field Training Officers
  The Field Training Officer has dual roles: as a fully responsible police officer and a trainer-coach. FTOs guide probationary officers while evaluating their progress and readiness for independent duty.
  
  - Incentives: FTOs earn compensatory time, receive an FTO pin, and are prioritized for relevant training.
  
  ---
  
  Supervisors' Responsibilities
  Supervisors oversee squad training and evaluation, ensuring that all procedures are followed. This includes report reviews and determining if remedial training is required.
  
  ---
  
  Program Documentation
  Proper documentation is essential to evaluate probationary officers' performance accurately. Supervisors and FTOs are responsible for maintaining these records.
  
  ---
  
  Evaluation Forms and Training Guide
  The Field Training Officer completes written daily evaluations and forwards them for review every five days. Probationary officers must receive satisfactory ratings for at least the last 15 days (or last 5 days for returning officers) to qualify for release from training.
  
  ---
  
  Officer Safety Warning Notice and Remedial Training Plan Worksheet
  These forms document officer safety concerns and remedial training efforts as part of the Field Training Manual.
  
  ---
  
  Conclusion
  This Field Training Officer Program Protocol outlines the Cheverly Police Department's commitment to fostering skilled, ethical, and professional officers through rigorous training, evaluation, and support.
  `,
}; 